<template>
  <div class="page-wrap">

    <!-- Processing overlay -->
    <transition name="fade">
      <div class="processing-overlay" v-if="saveDataLoader">
        <div class="processing-overlay__box">
          <div class="processing-overlay__spinner"></div>
          <span>Sending email…</span>
        </div>
      </div>
    </transition>

    <!-- Page loading -->
    <div class="page-loading" v-if="getLoadingData">
      <div class="page-loading__spinner"></div>
      <span>Loading job details…</span>
    </div>

    <div v-if="!getLoadingData">

      <!-- Panel card -->
      <div class="panel-card">

        <!-- Header -->
        <div class="panel-header">
          <div class="panel-header__icon">
            <i class="fa fa-briefcase"></i>
          </div>
          <div class="panel-header__text">
            <div class="panel-header__title">
              {{ jobData.title }}
              <span class="status-badge status-badge--open" v-if="jobData.status == 'Open'">Open</span>
              <span class="status-badge status-badge--closed" v-else-if="jobData.status">{{ jobData.status }}</span>
            </div>
            <div class="panel-header__link" v-if="jobData.job_link">
              <i class="fa fa-link"></i>
              <a :href="jobData.job_link" target="_blank" class="job-link">{{ jobData.job_link }}</a>
              <button class="btn-copy" @click="copyLink" title="Copy link">
                <i class="fa fa-copy"></i>
              </button>
              <input type="hidden" id="job_link1" :value="jobData.job_link" />
            </div>
          </div>
          <div class="panel-header__actions">
            <button
              v-if="jobData.status == 'Open'"
              class="btn-mail"
              @click="SendJobMail(jobData.id)"
            >
              <i class="fa fa-envelope"></i> Send Mail
            </button>
            <button class="btn-edit" @click="editJob(jobData.id)">
              <i class="fa fa-pen"></i> Edit
            </button>
            <button class="btn-back" @click="goBack()">
              <i class="fa fa-arrow-left"></i> Back
            </button>
          </div>
        </div>

        <!-- Info cards row -->
        <div class="info-row">

          <!-- Job Details -->
          <div class="info-card">
            <div class="info-card__title">
              <i class="fa fa-info-circle"></i> Job Details
            </div>
            <div class="info-list">
              <div class="info-item">
                <span class="info-item__key">Service</span>
                <span class="info-item__val">{{ jobData.service_name || '—' }}</span>
              </div>
              <div class="info-item">
                <span class="info-item__key">Email Status</span>
                <span class="info-item__val">
                  <span class="mini-badge mini-badge--green" v-if="jobData.email_sent == 1">Sent</span>
                  <span class="mini-badge mini-badge--red" v-else>Not Sent</span>
                </span>
              </div>
              <div class="info-item">
                <span class="info-item__key">Schedule</span>
                <span class="info-item__val">
                  <span v-if="jobData.schedule_date">
                    {{ jobData.schedule_date | dateFormat }}
                    <span v-if="jobData.schedule_time"> · {{ jobData.schedule_time }}</span>
                  </span>
                  <span v-else class="info-item__na">N/A</span>
                </span>
              </div>
              <div class="info-item">
                <span class="info-item__key">Payment By</span>
                <span class="info-item__val">{{ jobData.payment_by || '—' }}</span>
              </div>
              <div class="info-item">
                <span class="info-item__key">Operator</span>
                <span class="info-item__val">{{ jobData.operator_name || '—' }}</span>
              </div>
              <div class="info-item">
                <span class="info-item__key">Service Location</span>
                <span class="info-item__val">{{ jobData.service_location_full_address || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Client Details -->
          <div class="info-card">
            <div class="info-card__title">
              <i class="fa fa-user"></i> Client Details
            </div>
            <div class="info-list">
              <div class="info-item">
                <span class="info-item__key">Name</span>
                <span class="info-item__val">{{ jobData.client_name || '—' }}</span>
              </div>
              <div class="info-item">
                <span class="info-item__key">Email</span>
                <span class="info-item__val">{{ jobData.client_email || '—' }}</span>
              </div>
              <div class="info-item">
                <span class="info-item__key">Address</span>
                <span class="info-item__val">{{ jobData.client_full_address || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Contact Details -->
          <div class="info-card">
            <div class="info-card__title">
              <i class="fa fa-address-card"></i> Contact Details
            </div>
            <div class="info-list">
              <div class="info-item">
                <span class="info-item__key">Name</span>
                <span class="info-item__val">{{ jobData.contact_person || '—' }}</span>
              </div>
              <div class="info-item">
                <span class="info-item__key">Email</span>
                <span class="info-item__val">{{ jobData.contact_email || '—' }}</span>
              </div>
              <div class="info-item">
                <span class="info-item__key">Phone</span>
                <span class="info-item__val">{{ jobData.contact_phone_no || '—' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Divider + Appointments header -->
        <div class="appt-header">
          <div class="section-label">
            <i class="fa fa-calendar-alt"></i> Appointments
          </div>
          <div class="appt-header__actions">
            <div class="applicant-search">
              <i class="fa fa-search"></i>
              <input
                type="text"
                v-model="search_text"
                placeholder="Search by applicant name…"
                @keyup.enter="search()"
              />
              <button v-if="search_text" class="applicant-search__clear" @click="clearSearch()" title="Clear">
                <i class="fa fa-times"></i>
              </button>
            </div>
            <button class="btn-search" @click="search()">
              <i class="fa fa-search"></i> Search
            </button>
            <button
              class="btn-refresh"
              @click="refreshData()"
              v-if="jobAppointments.length > 0 && !getLoadingAppointment"
            >
              <i class="fa fa-sync-alt"></i> Refresh
            </button>
          </div>
        </div>

        <!-- Appointment modal -->
        <appointmentDetailModal
          :key="key"
          v-if="appointmentDetailModalShow"
          v-on:closemodal="closeModal"
          v-on:updatepaymentstatus="updatepaymentstatus"
          :loading-data="loadingData"
          :has-data="hasData"
          :show-modal="appointmentDetailModalShow"
          :appointment-data="appointmentData"
          :index-num="currentIndex"
          :show-qr-code="showQrCode"
          :token="token"
          @reLoadTable="reTableReload"
        ></appointmentDetailModal>

        <!-- Appointments loading -->
        <div class="appt-loading" v-if="getLoadingAppointment">
          <div class="appt-loading__spinner"></div>
          <span>Loading appointments…</span>
        </div>

        <!-- Appointments table -->
        <div class="table-wrap" v-if="jobAppointments.length > 0 && !getLoadingAppointment">
          <table class="appt-table">
            <thead>
              <tr>
                <th>App Date</th>
                <th>Enr. Date</th>
                <th class="th-right">App No.</th>
                <th class="th-right">Enr. No</th>
                <th>Applicant</th>
                <th class="th-right">Amount</th>
                <th class="th-center">Status</th>
                <th>Payment</th>
                <th class="th-center">Paid</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in jobAppointments"
                :key="index"
                class="appt-row"
                :class="{ 'appt-row--deleted': item.e_deleted }"
              >
                <td class="td-date">{{ datetimeFormatAmPm(item.created_at) }}</td>
                <td class="td-date">{{ item.enroll_date ? datetimeFormatAmPm(item.enroll_date) : '—' }}</td>
                <td class="td-right">
                  <a class="appt-link" href="javascript:void(0)" @click="openAppointmentDetail(item.id, item.token, index, true)">
                    {{ item.id }}
                  </a>
                </td>
                <td class="td-right">{{ item.enrollment_id }}</td>
                <td>{{ item.applicant }}</td>
                <td class="td-right">${{ item.amount | toFixedTwoDigit }}</td>
                <td class="td-center">
                  <!-- Inline status editor -->
                  <div class="status-editor" v-if="item.change_status_flag && item.appt_status != 'Cancel'">
                    <select class="status-select" v-model="item.new_status">
                      <option value="P">Pending</option>
                      <option value="C">Completed</option>
                      <option value="U">No-Show</option>
                      <option value="Cancel">Cancel</option>
                    </select>
                    <button
                      v-if="item.appt_status != item.new_status"
                      class="status-confirm"
                      @click="updateStatusChangesFlag(item.id, index, item.new_status)"
                    ><i class="fa fa-check"></i></button>
                    <button class="status-cancel" @click="closeChangeStatusFlag(index)">
                      <i class="fa fa-times"></i>
                    </button>
                  </div>
                  <span
                    v-if="!item.change_status_flag"
                    class="appt-status-badge"
                    :class="'appt-status-badge--' + statusColor(item.appt_status)"
                    style="cursor: pointer"
                    @click="changeStatusOpen(item.id, index)"
                  >{{ item.appt_status | apptStatus }}</span>
                </td>
                <td>
                  <span
                    class="payment-badge"
                    style="cursor: pointer"
                    @click="openAppointmentDetail(item.id, item.token, index, false)"
                  >{{ item.payment_type | payementStatus }}</span>
                </td>
                <td class="td-center">
                  <i class="fa fa-check paid-icon paid-icon--yes" v-if="item.payment_status == 'P'"></i>
                  <i class="fa fa-times paid-icon paid-icon--no" v-if="item.payment_status == 'U'"></i>
                  <span class="mini-badge mini-badge--red" v-if="item.payment_status == 'Refund'">Refunded</span>
                </td>
                <td class="td-action">
                  <a
                    v-if="item.payment_status == 'P' && item.appt_status != 'Cancel'"
                    href="javascript:void(0)"
                    class="btn-invoice"
                    @click="openInvoice(item.token)"
                  >Invoice <i class="fa fa-arrow-right"></i></a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty appointments -->
        <div class="empty-state" v-if="jobAppointments.length < 1 && !getLoadingAppointment">
          <i class="fa fa-calendar-times empty-state__icon"></i>
          <div class="empty-state__title">No appointments found</div>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrap" v-if="pagination.total_page > 1">
          <vl-pagination
            v-on:selected="selectedPage"
            v-on:previous="previousPage"
            v-on:first="firstPage"
            v-on:last="lastPage"
            v-on:next="nextPage"
            :pagination="pagination"
          ></vl-pagination>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import moment from "moment";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import {
  FETCH_SELF_APPOINMENT,
  APPOINTMENT_LOGS,
  FETCH_CLIENT_JOB_DETAILS,
  SEND_CLIENT_JOB_MAIL,
  UPDATE_APPOINTMENT_STATUS,
  FETCH_CLIENT_JOB_APPOINTMENTLIST
} from "@/core/services/store/actions.type";
import * as PDF417 from "pdf417-generator";
import vlPagination from "@/includes/table/Pagination";
import appointmentDetailModal from "@/components/frontend/modal/appointmentdetail";

export default {
  components: {
    appointmentDetailModal,
    vlPagination,
  },
  computed: {
    ...mapGetters(["currentUser", "getJobDetails"])
  },
  filters: {
    toFixedTwoDigit(amount) {
      return parseFloat(amount).toFixed(2);
    },
    timeFormat: function(value, date) {
      if (date == null) return;
      var fullDate = date + " " + value;
      return moment(fullDate).format("hh:mm a");
    },
    dateFormat(date) {
      if (date == null) return;
      return moment(date).format("MM/DD/YYYY");
    },
    payementStatus(status) {
      if (status == "C") return "Card";
      if (status == "A") return "Abbreviation";
      if (status == "O") return "On-site";
      if (status == "CP") return "Company Payment";
      if (status == "Cancel") return "Cancel";
    },
    apptStatus(status) {
      switch (status) {
        case "P": return "Pending";
        case "C": return "Completed";
        case "U": return "No-Show";
        case "Cancel": return "Canceled";
        default: return status;
      }
    }
  },
  data() {
    return {
      jobData: [],
      getLoadingAppointment: true,
      pagination: {
        total_records: 0,
        total_page: 0,
        current_page: 1,
        per_page_data: 15,
        received_per_page_data: 15
      },
      appointmentDetailModalShow: false,
      jobAppointments: [],
      search_text: "",
      client_slug: "",
      selectedAppId: 0,
      showQrCode: false,
      currentIndex: -1,
      loadingData: false,
      hasData: false,
      getLoadingData: true,
      saveDataLoader: false,
      key: 0,
      token: "",
      appointmentData: null,
      loadingMessage: "",
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Mobile Service", route: "client.job.list" },
        { id: 3, title: "Details", route: "" }
      ]
    };
  },
  created() {
    this.client_slug = this.$route.params.client_slug;
    this.job_id = this.$route.params.job_id;
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getLoadingData = true;
    this.$store
      .dispatch(FETCH_CLIENT_JOB_DETAILS, { id: parseInt(this.job_id), list: true })
      .then(data => {
        this.getLoadingData = false;
        this.jobData = data.data;
      });
    this.getData();
  },
  methods: {
    refreshData() {
      this.pagination = {
        total_records: 0,
        total_page: 0,
        current_page: 1,
        per_page_data: 15,
        received_per_page_data: 15
      };
      this.getData();
    },
    async getData() {
      this.getLoadingAppointment = true;
      await this.$store
        .dispatch(FETCH_CLIENT_JOB_APPOINTMENTLIST, {
          id: parseInt(this.job_id),
          page: this.pagination.current_page,
          search_text: this.search_text
        })
        .then(data => {
          this.getLoadingAppointment = false;
          this.pagination.total_records = data.total_record;
          this.pagination.total_page = data.page_range;
          this.pagination.received_per_page_data = data.data.length;
          this.jobAppointments = data.data;
        });
    },
    closeModal() {
      this.appointmentDetailModalShow = false;
    },
    editJob(id) {
      this.$router.push({
        name: "client.job.form",
        params: { client_slug: this.client_slug, job_id: id }
      });
    },
    SendJobMail(id) {
      this.loadingMessage = "please wait <br> While sending the email...";
      var data = { id: id };
      this.saveDataLoader = true;
      this.$store
        .dispatch(SEND_CLIENT_JOB_MAIL, data)
        .then(res => {
          this.saveDataLoader = false;
          this.jobData.email_sent = res.data.email_sent;
          this.$toastr.s(res.msg);
        })
        .catch(res => {
          this.saveDataLoader = false;
          this.$toastr.e(res);
        });
    },
    openAppointmentDetail(appId, token, index, showQrCode) {
      if (this.jobAppointments[index].cancel == 1 && this.currentUser.u.is_client) {
        return 0;
      }
      this.showQrCode = showQrCode;
      this.currentIndex = index;
      this.appointmentDetailModalShow = true;
      this.selectedAppId = appId;
      this.loadingData = true;
      var data = { token: token, appointment_id: appId };

      this.$store
        .dispatch(FETCH_SELF_APPOINMENT, data)
        .then(res => {
          this.appointmentData = res.data;
          this.token = token;
          this.loadingData = false;
          this.hasData = res.hasData;
          this.key += 1;

          if (showQrCode) {
            var DBB = this.$moment(res.data.dob).format("MMDDYYYY");
            var DBC = res.data.gender == "Female" ? 2 : 1;
            var DAU = ((res.data.height * 12) / 100).toFixed(2);
            var value =
              "@\n\nANSI 636020090102DL00410279ZC03200010DLDAQ171774992\nDCA\nDCB\nDCD\nDBA\nDCS" +
              res.data.lname + "\nDAC" + res.data.fname + "\nDAD" + res.data.mname +
              "\nDBD\nDBB" + DBB + "\nDBC" + DBC + "\nDAY" + res.data.eye_color +
              "\nDAU0" + DAU * 100 + " in\nDAG" + "\nDAI" + res.data.city +
              "\nDAJ" + res.data.address + "\nDAK" + res.data.zipcode +
              "\nDAQ\nDCF\nDCG\nDDE\nDDF\nDDG";
            var canvas = document.getElementById("barcode");
            PDF417.draw(value, canvas);
            res.data.barcode_base64 = canvas.toDataURL("image/png");
          }

          this.appointmentData = res.data;
          this.$store.dispatch(APPOINTMENT_LOGS, { app_id: appId });
        })
        .catch(() => {
          this.loadingData = false;
        });
    },
    closeChangeStatusFlag(index) {
      this.selectedAppId = 0;
      this.jobAppointments[index].change_status_flag = false;
    },
    updateStatusChangesFlag(appId, index, appt_status) {
      var status = this.jobAppointments[index].new_status;
      if (status != this.jobAppointments[index].appt_status) {
        if (status == "Cancel") {
          let message = `You want to cancel the appointment no. ${this.jobAppointments[index].id}`;
          this.$swal.fire({
            title: "Are you sure?",
            text: message,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes",
            cancelButtonText: "No"
          }).then(result => {
            if (result.isConfirmed) {
              this.storeStatusChange(appId, appt_status, index, status);
            } else {
              this.closeChangeStatusFlag(index);
            }
          });
        } else {
          this.storeStatusChange(appId, appt_status, index, status);
        }
      } else {
        this.jobAppointments[index].appt_status = status;
        this.closeChangeStatusFlag(index);
      }
    },
    storeStatusChange(appId, appt_status, index, status) {
      this.$store
        .dispatch(UPDATE_APPOINTMENT_STATUS, { app_id: appId, appt_status: appt_status })
        .then(res => {
          this.jobAppointments[index].appt_status = status;
          if (status == "Cancel") {
            this.jobAppointments[index].cancel = 1;
          } else {
            this.jobAppointments[index].cancel = 0;
          }
          this.closeChangeStatusFlag(index);
          this.$toastr.s(res.msg);
        })
        .catch(err => {
          this.$toastr.e(err.msg);
        });
    },
    copyLink() {
      let testingCodeToCopy = document.querySelector("#job_link1");
      testingCodeToCopy.setAttribute("type", "text");
      testingCodeToCopy.select();
      try {
        document.execCommand("copy");
        this.$toastr.s("Link copied successfully");
      } catch (err) {
        alert("Oops, unable to copy");
      }
      testingCodeToCopy.setAttribute("type", "hidden");
      window.getSelection().removeAllRanges();
    },
    openInvoice(token) {
      this.$router.push({
        name: "client.invoice.details",
        params: { slug: this.$route.params.client_slug, token: token }
      });
    },
    updatepaymentstatus(i, ps, is_drug_testing) {
      if (is_drug_testing) {
        this.refreshData();
      }
      this.jobAppointments[i].payment_status = ps;
    },
    changeStatusOpen(appointMentId, index) {
      if (this.jobAppointments[index].cancel == 0) {
        this.selectedAppId = appointMentId;
        this.jobAppointments[index].change_status_flag = true;
      }
    },
    search() {
      this.pagination.current_page = 1;
      this.getData();
    },
    clearSearch() {
      this.search_text = "";
      this.search();
    },
    selectedPage(n) {
      this.pagination.current_page = n;
      this.getData();
    },
    nextPage() {
      this.pagination.current_page = this.pagination.current_page + 1;
      this.getData();
    },
    lastPage() {
      this.pagination.current_page = this.pagination.total_page;
      this.getData();
    },
    firstPage() {
      this.pagination.current_page = 1;
      this.getData();
    },
    previousPage() {
      this.pagination.current_page = this.pagination.current_page - 1;
      this.getData();
    },
    goBack() {
      this.$router.push({ name: "client.job.list" });
    },
    statusColor(status) {
      if (status == "P") return "warning";
      if (status == "C") return "success";
      if (status == "U") return "info";
      if (status == "Cancel") return "danger";
    },
    datetimeFormatAmPm(date) {
      return moment(date).format("MM/DD/YY hh:mm a");
    },
    reTableReload() {
      this.refreshData();
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$green:  #276749;
$border: #dde2ec;

/* ── overlay ── */
.processing-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(3px);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;

  &__box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    background: #fff;
    border: 1px solid $border;
    border-radius: 14px;
    padding: 32px 40px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.1);
    font-family: $font;
    font-size: 13px;
    color: $dark;
    font-weight: 500;
  }

  &__spinner {
    width: 36px;
    height: 36px;
    border: 3.5px solid #c5d8f5;
    border-top-color: $blue;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
}

@keyframes spin { to { transform: rotate(360deg); } }
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ── page loading ── */
.page-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 24px;
  font-family: $font;
  font-size: 13px;
  color: #7a8499;

  &__spinner {
    width: 24px;
    height: 24px;
    border: 2.5px solid #c5d8f5;
    border-top-color: $blue;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
}

/* ── page wrap ── */
.page-wrap {
  padding: 12px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── panel header ── */
.panel-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__text {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 5px;
    font-size: 12px;
    color: #7a8499;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    flex-wrap: wrap;
  }
}

.job-link {
  font-size: 12px;
  color: $blue;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 400px;

  &:hover { text-decoration: underline; }
}

.btn-copy {
  background: none;
  border: none;
  color: #a0aab8;
  cursor: pointer;
  padding: 0 4px;
  font-size: 13px;
  transition: color 0.15s;
  &:hover { color: $blue; }
}

/* ── header buttons ── */
.btn-mail, .btn-edit, .btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 500;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
  border: 1.5px solid transparent;
}

.btn-mail {
  background: rgba(56,154,212,0.1);
  color: $blue;
  border-color: rgba(56,154,212,0.25);
  &:hover { background: $blue; color: #fff; border-color: $blue; }
}

.btn-edit {
  background: rgba(39,103,73,0.1);
  color: $green;
  border-color: rgba(39,103,73,0.2);
  &:hover { background: $green; color: #fff; border-color: $green; }
}

.btn-back {
  background: #f0f3fa;
  color: $dark;
  border-color: $border;
  &:hover { background: #e4eaf5; }
}

/* ── status badges ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;

  &--open { background: #e6f4ec; color: $green; }
  &--closed { background: #fdecea; color: #b42318; }
}

/* ── info cards row ── */
.info-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid $border;
}

.info-card {
  padding: 20px 24px;
  border-right: 1px solid $border;

  &:last-child { border-right: none; }

  &__title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: $blue;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

.info-list { display: flex; flex-direction: column; gap: 10px; }

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;

  &__key {
    font-size: 11px;
    color: #9aa0b0;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__val {
    font-size: 13px;
    color: $dark;
  }

  &__na { color: #c0c8d8; font-style: italic; }
}

/* ── mini badges ── */
.mini-badge {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;

  &--green { background: #e6f4ec; color: $green; }
  &--red   { background: #fdecea; color: #b42318; }
}

/* ── appointments header ── */
.appt-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px 8px;
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $blue;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-refresh {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0f3fa;
  color: $dark;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #e4eaf5; }
}

.appt-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.applicant-search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  min-width: 220px;

  i.fa-search {
    font-size: 11px;
    color: #a0aab8;
    flex-shrink: 0;
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 12px;
    font-family: $font;
    color: $dark;
    min-width: 0;

    &::placeholder { color: #b0bac9; }
  }
}

.applicant-search__clear {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #a0aab8;
  cursor: pointer;
  padding: 0;
  font-size: 11px;
  flex-shrink: 0;
  &:hover { color: #e34c4c; }
}

.btn-search {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: $blue;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.9; }
}

/* ── appointments loading ── */
.appt-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 24px;
  font-size: 13px;
  color: #7a8499;

  &__spinner {
    width: 18px;
    height: 18px;
    border: 2.5px solid #c5d8f5;
    border-top-color: $blue;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }
}

/* ── table ── */
.table-wrap {
  overflow-x: auto;
  padding: 0 24px 24px;
}

.appt-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: $dark;

  th {
    padding: 10px 12px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #7a8499;
    border-bottom: 2px solid $border;
    white-space: nowrap;
    background: #fafbfd;
  }

  .th-right  { text-align: right; }
  .th-center { text-align: center; }

  td {
    padding: 11px 12px;
    border-bottom: 1px solid #f0f2f8;
    vertical-align: middle;
  }

  .appt-row {
    transition: background 0.12s;
    &:hover { background: #f8fbff; }
    &:last-child td { border-bottom: none; }

    &--deleted { background: #ffeeee; }
    &--deleted:hover { background: #ffdcdc; }
  }

  .td-date  { font-size: 12px; color: #5a6480; white-space: nowrap; }
  .td-right { text-align: right; }
  .td-center { text-align: center; }
  .td-action { text-align: right; white-space: nowrap; }
}

.appt-link {
  color: $blue;
  text-decoration: none;
  &:hover { text-decoration: underline; }
}

/* ── appointment status badge ── */
.appt-status-badge {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;

  &--warning { background: #fff8e1; color: #b45309; }
  &--success { background: #e6f4ec; color: $green; }
  &--info    { background: #e8f4fd; color: #1a6fa8; }
  &--danger  { background: #fdecea; color: #b42318; }
}

/* ── inline status editor ── */
.status-editor {
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-select {
  height: 30px;
  border: 1.5px solid $border;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 12px;
  font-family: $font;
  color: $dark;
  background: #fafbfd;
  outline: none;

  &:focus { border-color: $blue; }
}

.status-confirm,
.status-cancel {
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  transition: background 0.15s;
}

.status-confirm {
  background: #e6f4ec;
  color: $green;
  &:hover { background: $green; color: #fff; }
}

.status-cancel {
  background: #fdecea;
  color: #b42318;
  &:hover { background: #b42318; color: #fff; }
}

/* ── payment badge ── */
.payment-badge {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(56,154,212,0.12);
  color: $blue;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: $blue; color: #fff; }
}

/* ── paid icons ── */
.paid-icon {
  font-size: 14px;
  &--yes { color: $green; }
  &--no  { color: #b42318; }
}

/* ── invoice button ── */
.btn-invoice {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: $green;
  text-decoration: none;
  background: rgba(39,103,73,0.1);
  border-radius: 6px;
  padding: 4px 10px;
  transition: background 0.15s;

  &:hover { background: $green; color: #fff; text-decoration: none; }
}

/* ── empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 44px 24px;
  gap: 8px;

  &__icon { font-size: 36px; color: #c5d0e0; }
  &__title { font-size: 14px; font-weight: 500; color: #7a8499; }
}

/* ── pagination ── */
.pagination-wrap {
  padding: 0 24px 20px;
  border-top: 1px solid $border;
  padding-top: 16px;
}
</style>
